import LocationPanels, {LocationPanelProvider} from "./LocationPanels";
import Call from "./Call";
import {useQuery} from "@tanstack/react-query";
import {getRecentCalls} from "../api/calls";
import {useLayoutEffect, useRef, useState} from "react";
import {PaginationAndFilter} from "./PaginationAndFilter";
import Tabs from "./Tabs";
import GroupOverview from "./GroupOverview";

const CallsContainer = () => {
    const [queryParams, setQueryParams] = useState({
        start: 0,
        pageSize: 10,
        from: null,
        to: null,
        lat: null,
        lng: null,
        sourceIdentifier2: ""
    });

    const listRef = useRef(null);
    const scrollTopRef = useRef(0);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [selectedCall, setSelectedCall] = useState(null);
    const [filters, setFilters] = useState({from: null, to: null, lat: null, lng: null, sourceIdentifier2: ""});
    const applyFilters = (values = filters) => {
        setSelectedCall(null);
        setQueryParams({...queryParams, ...values, sourceIdentifier2: values.sourceIdentifier2.trim(), start: 0});
    };
    const clearFilters = () => {
        const cleared = {from: null, to: null, lat: null, lng: null, sourceIdentifier2: ""};
        setFilters(cleared);
        // Apply the cleared values directly; React has not committed the draft reset yet.
        applyFilters(cleared);
    };
    const goPrevious = () => {
        let previousStart = queryParams.start - queryParams.pageSize;
        previousStart = previousStart < 0 ? 0 : previousStart;
        setQueryParams({...queryParams, start: previousStart, pageSize: queryParams.pageSize});
    }
    const goNext = () => {
        setQueryParams({
            ...queryParams,
            start: queryParams.start + queryParams.pageSize,
            pageSize: queryParams.pageSize
        });
    }
    const setFrom = (from) => setFilters(previous => ({...previous, from}));
    const setTo = (to) => setFilters(previous => ({...previous, to}));
    const setLat = (lat) => setFilters(previous => ({...previous, lat}));
    // Group uses the same draft/apply lifecycle as the existing date and coordinate filters.
    const setSourceIdentifier2 = value => setFilters(previous => ({...previous, sourceIdentifier2: value}));
    const setLng = (lng) => setFilters(previous => ({...previous, lng}));

    const {isLoading, isRefetching, isError, data} = useQuery({
        queryKey: ["get-calls", {
            start: queryParams.start,
            pageSize: queryParams.pageSize,
            from: queryParams.from,
            to: queryParams.to,
            lat: queryParams.lat,
            lng: queryParams.lng,
            sourceIdentifier2: queryParams.sourceIdentifier2
        }],
        queryFn: getRecentCalls,
        keepPreviousData: true,
        refetchInterval: 30000, // refetch every 30 sec,
        refetchIntervalInBackground: true
    });
    // Restore the user's offset after fresh results change the list's contents.
    useLayoutEffect(() => {
        if (listRef.current) listRef.current.scrollTop = scrollTopRef.current;
    }, [data]);

    const dataToRender = data || {hasNext: false, hasPrevious: false, calls: []};

    return <LocationPanelProvider><div className={`calls-workspace${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
        <aside className="calls-sidebar" aria-labelledby="calls-title">
            <div className="calls-sidebar-header">
                <h1 id="calls-title" className="panel-title">Calls</h1>
                <button type="button" className="panel-collapse-button" aria-controls="calls-sidebar-content"
                    aria-expanded={!sidebarCollapsed} aria-label={sidebarCollapsed ? 'Show calls sidebar' : 'Hide calls sidebar'}
                    title={sidebarCollapsed ? 'Show calls sidebar' : 'Hide calls sidebar'}
                    onClick={() => setSidebarCollapsed(value => !value)}>{sidebarCollapsed ? '›' : '‹'}</button>
            </div>
            <div id="calls-sidebar-content" className="calls-sidebar-content" hidden={sidebarCollapsed}>
            <PaginationAndFilter fromDate={filters.from} toDate={filters.to}
                setFromDate={setFrom} setToDate={setTo}
                lat={filters.lat} lng={filters.lng} setLat={setLat} setLng={setLng}
                sourceIdentifier2={filters.sourceIdentifier2} setSourceIdentifier2={setSourceIdentifier2}
                applyFilters={applyFilters} clearFilters={clearFilters} isLoading={isLoading || isRefetching} isError={isError}
                goPrevious={goPrevious} goNext={goNext}
                hasNext={dataToRender.hasNext} hasPrevious={dataToRender.hasPrevious}/>
            <div className="calls-list" ref={listRef}
                 onScroll={event => {scrollTopRef.current = event.currentTarget.scrollTop;}}>
                {dataToRender.calls.map(call => <Call key={call.callUuid} {...call}
                    selected={selectedCall === call.callUuid} onSelect={() => setSelectedCall(call.callUuid)}/>)}
                {!isLoading && !isError && !dataToRender.calls.length && <p className="panel-message">No calls found</p>}
            </div>
            </div>
        </aside>
        {/* A nonempty applied group opts into overview mode; otherwise retain the existing view. */}
        {queryParams.sourceIdentifier2 ? <GroupOverview filters={queryParams} selectedCall={selectedCall}
            onSelect={setSelectedCall}/> : selectedCall ? <Tabs key={selectedCall} callUuid={selectedCall} isOpen={true}/> :
            <div className="call-details">
                <section className="map-panel empty-map" aria-label="Map">Select call</section>
                <LocationPanels>
                    <section className="location-panel"><h2 className="panel-title">Current Location</h2></section>
                    <section className="history-panel"><h2 className="panel-title">History</h2></section>
                </LocationPanels>
            </div>}
    </div></LocationPanelProvider>;
};
export default CallsContainer;
